import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./sitedetails.css";

const labels = {
  name: "Name",
  address: "Address",
  notificationService: "Notification Service",
  pmrSystemIdStr: "PMR System",
  showShelfWeb: "StoS",
  showAddressWeb: "Add Bk",
  showDeliveryWeb: "Del",
  showFridgeWeb: "Fridge",
  showHandoverDiaryWeb: "Handover",
  showIncidentWeb: "Med Inc",
  showNearMissWeb: "Near Miss",
  allowPhoneNumberUpdate: "QR Ph.No.",
  includeFridge: "Fridge",
  includeNearmiss: "Near Miss",
  useSimCardMessaging: "SIM Mess",
  email: "Login Email",
  uid: "UID",
  dbName: "DB Name",
  loginCode: "Login Code",
  createdAt: "Created",
  lastLogin: "Last Login",
  devices: "Devices"
};

const Sitedetails = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [authenticated, setAuthenticated] = useState("");
  const [popupContent, setPopupContent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 12;

  const load = async () => {
    if (authenticated !== "pharmace2023") {
      const pwd = prompt("Enter password to view site details:");
      if (pwd !== "pharmace2023") {
        alert("Incorrect password");
        return;
      }
      setAuthenticated(pwd);
    }

    setLoading(true);
const token = localStorage.getItem("authToken");

try {
  const res = await axios.get("https://api.pharmacegroup.co.uk/sitedetails", {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  setData(res.data);
  setFiltered(res.data);
  console.log("✅ Loaded site data:", res.data);
} catch (err) {
  console.error("❌ Error fetching site data:", err);
  setError(err.message);
} finally {
  setLoading(false);
}
  };

  useEffect(() => {
    let filteredData = data.filter((entry) =>
      Object.values(entry).some((val) => {
        if (typeof val === "object") {
          return JSON.stringify(val).toLowerCase().includes(searchText.toLowerCase());
        }
        return String(val).toLowerCase().includes(searchText.toLowerCase());
      })
    );

    if (sortField) {
      filteredData.sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];

        if (typeof aVal === "boolean") {
          return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
        }

        return sortDirection === "asc"
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }

    setFiltered(filteredData);
    setCurrentPage(1);
  }, [searchText, sortField, sortDirection, data]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sites");
    const blob = XLSX.write(wb, { bookType: "xlsx", type: "blob" });
    saveAs(blob, "sitedetails.xlsx");
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date)) return "-";
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yy = String(date.getFullYear()).slice(-2);
      return `${dd}/${mm}/${yy}`;
    } catch {
      return "-";
    }
  };

  const formatDevices = (deviceArray) => {
    if (!Array.isArray(deviceArray)) return "Invalid device data.";
    return deviceArray.map((device, index) => {
      const entries = Object.entries(device)
        .map(([key, val]) => `${key}: ${val}`)
        .join("\n");
      return `Device #${index + 1}\n${entries}`;
    }).join("\n\n");
  };

  const renderCell = (key, value, columnIndex) => {
    const backgroundColor = columnIndex % 2 === 0 ? "#ffffff" : "#f7f7f7";

    if (typeof value === "boolean") {
      return (
        <td style={{ backgroundColor, textAlign: "center", whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={value} readOnly style={{ backgroundColor: value ? "lightgreen" : "white" }} />
        </td>
      );
    }

    if (key === "devices" && Array.isArray(value)) {
      return (
        <td
          style={{ backgroundColor, whiteSpace: "nowrap", cursor: "pointer", textAlign: "center" }}
          title="Click to view full device details"
          onClick={() => setPopupContent(formatDevices(value))}
        >
          {value.length}
        </td>
      );
    }

    if (key === "address") {
      const short = value?.slice(0, 30);
      const hasMore = value?.length > 30;
      return (
        <td title={value} style={{ backgroundColor, whiteSpace: "nowrap" }}>
          {short}{hasMore ? "..." : ""}
        </td>
      );
    }

    if (key === "createdAt" || key === "lastLogin") {
      return (
        <td style={{ backgroundColor, whiteSpace: "nowrap" }}>
          {value ? formatDate(value) : "-"}
        </td>
      );
    }

    return <td style={{ backgroundColor, whiteSpace: "nowrap" }}>{value ?? "-"}</td>;
  };

  const startIdx = (currentPage - 1) * recordsPerPage;
  const paginated = filtered.slice(startIdx, startIdx + recordsPerPage);
  const totalPages = Math.ceil(filtered.length / recordsPerPage);

  return (
    <div style={{ padding: 20 }}>
      <h2>Site Details</h2>

      <button onClick={load} style={{ marginRight: 10 }}>🔄 Refresh</button>
      <button onClick={exportToExcel}>📁 Export to Excel</button>

      <div style={{ marginTop: 10 }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            padding: "8px",
            marginBottom: "10px",
            width: "300px",
            fontSize: "16px"
          }}
        />
      </div>

      {loading && <p>Loading...</p>}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}

      {data.length > 0 && !loading && !error && (
        <div>
          <div style={{ textAlign: "right", marginBottom: "4px", fontWeight: "bold" }}>
            Showing {filtered.length} record{filtered.length !== 1 ? "s" : ""}
          </div>
          <div style={{ overflowX: "auto", maxHeight: "80vh", overflowY: "auto" }}>
            <table className="site-table">
              <thead style={{ position: "sticky", top: 0, background: "#f2f2f2", zIndex: 1 }}>
                <tr>
                  {Object.keys(labels).map((key, index) => (
                    <th
                      key={key}
                      style={{
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        position: key === "name" ? "sticky" : "static",
                        left: key === "name" ? 0 : undefined,
                        backgroundColor: key === "name" ? "#f9f9f9" : undefined,
                        zIndex: key === "name" ? 2 : 1
                      }}
                      onClick={() => handleSort(key)}
                    >
                      {labels[key]}
                      {sortField === key ? (sortDirection === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((site, rowIndex) => (
                  <tr key={rowIndex}>
                    {Object.keys(labels).map((key, columnIndex) =>
                      renderCell(key, site[key], columnIndex)
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ marginTop: 10, textAlign: "center" }}>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  style={{
                    margin: "0 4px",
                    padding: "6px 12px",
                    backgroundColor: currentPage === i + 1 ? "#007bff" : "#f2f2f2",
                    color: currentPage === i + 1 ? "white" : "black",
                    border: "1px solid #ccc",
                    borderRadius: "4px"
                  }}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {popupContent && (
        <div
          style={{
            position: "fixed",
            top: "20%",
            left: "30%",
            width: "40%",
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            padding: "20px",
            boxShadow: "0 0 10px rgba(0,0,0,0.2)",
            zIndex: 9999,
            whiteSpace: "pre-wrap",
            maxHeight: "60vh",
            overflowY: "auto"
          }}
        >
          <h4>Device Details</h4>
          <pre>{popupContent}</pre>
          <button onClick={() => setPopupContent(null)} style={{ marginTop: 10 }}>Close</button>
        </div>
      )}
    </div>
  );
};

export default Sitedetails;
